import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, finalize, tap, throwError, forkJoin } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../types/user';
import { Patient } from '../types/patient';
import { Institute } from '../types/institute';
import {
  SubmitRequestFormData,
  SaveRequestPayload,
  SaveRequestResponse,
  LogNumberResponse
} from '../types/request';
import { Form } from '../types/form';
import { Relations } from '../types/doc-wizard';
import { PatientService } from './patient';
import { FormService } from './form-service';
import { UserServices } from './user-services';
import { InstituteServices } from './institute-services';
import { GetPdfForReferringRequest } from '../types/getPdfForReferringRequest';

@Injectable({
  providedIn: 'root'
})
export class RequestService {
  private readonly http = inject(HttpClient);
  private readonly patientService = inject(PatientService);
  private readonly formService = inject(FormService);
  private readonly userServices = inject(UserServices);
  private readonly instituteServices = inject(InstituteServices);
  private readonly apiUrl = environment.apiUrl;

  // State signals
  selectedReferrerDoctor = signal<User | null>(null);
  selectedRelation = signal<Relations | null>(null);
  submitRequestFormData = signal<SubmitRequestFormData | null>(null);
  currentFormCode = signal<string | null>(null);
  logNumber = signal<string | null>(null);
  caseId = signal<number | null>(null);
  externalServiceId = signal<string | null>(null);

  // Edit mode - original data (readonly)
  originalDoctorId = signal<number | null>(null);
  originalServicePointId = signal<number | null>(null);
  loadedDoctor = signal<User | null>(null);
  loadedServicePoint = signal<Institute | null>(null);

  // Mode control
  // Controls whether doctor/service point fields are locked during edit mode
  // When true: Submit form is readonly, dynamic form remains editable
  // Use case: Can't change who submitted the request after initial submission
  submitterLocked = signal<boolean>(false);

  // Loading states
  loadingStates = signal({
    savingRequest: false,
    loadingReferrerDoctors: false,
    loadingDoctorAndServicePoint: false
  });

  getReferrerDoctors(spId: number): Observable<User[]> {
    this.loadingStates.set({ ...this.loadingStates(), loadingReferrerDoctors: true });
    return this.http.get<User[]>(`${this.apiUrl}/rest/user/find-all-referrer-doctors/${spId}`).pipe(
      finalize(() => {
        this.loadingStates.set({ ...this.loadingStates(), loadingReferrerDoctors: false });
      })
    );
  }

  getNextLogNumber(): Observable<LogNumberResponse> {
    return this.http.get<LogNumberResponse>(`${this.apiUrl}/rest/sp-log-number/get-next-log-number`);
  }

  loadDoctorAndServicePointData(doctorId: number, servicePointId: number): Observable<{ doctor: User, servicePoint: Institute }> {
    this.loadingStates.set({ ...this.loadingStates(), loadingDoctorAndServicePoint: true });

    return forkJoin({
      doctor: this.userServices.findUserById(doctorId),
      servicePoint: this.instituteServices.getInstituteById(servicePointId)
    }).pipe(
      tap(({ doctor, servicePoint }) => {
        this.originalDoctorId.set(doctorId);
        this.originalServicePointId.set(servicePointId);
        this.loadedDoctor.set(doctor);
        this.loadedServicePoint.set(servicePoint);
        this.selectedReferrerDoctor.set(doctor);
      }),
      finalize(() => {
        this.loadingStates.set({ ...this.loadingStates(), loadingDoctorAndServicePoint: false });
      })
    );
  }

  buildSaveRequestPayload(
    patient: Patient,
    currentUser: User,
  ): SaveRequestPayload | null {
    // Validate all required data is present
    const doctor = this.selectedReferrerDoctor();
    const relation = this.selectedRelation();
    const formData = this.formService.requestForm();
    const logNum = this.logNumber();

    if (!patient || !doctor || !relation || !formData || !logNum || !currentUser?.servicePoint) {
      console.error('Missing required data for save request:', {
        hasPatient: !!patient,
        hasDoctor: !!doctor,
        hasRelation: !!relation,
        hasFormData: !!formData,
        hasLogNumber: !!logNum,
        hasServicePoint: !!currentUser?.servicePoint
      });
      return null;
    }

    const payload: SaveRequestPayload = {
      patientId: patient.id,
      servicePointId: currentUser.servicePoint.id!,
      logNumber: logNum.split('/').pop() ?? logNum,
      reqFormId: relation.reqId,
      resFormId: relation.resId,
      relationId: relation.id!,
      version: formData?.version || '',
      element: formData || {} as Form,
      referralId: doctor.id, // sender doctor id (renamed from doctorId)
    };

    // Add caseId if it exists (for modify requests)
    const caseId = this.caseId();
    if (caseId !== null) {
      payload.caseId = caseId;
    }

    // Add external service ID if present (for external token-based requests)
    const extServiceId = this.externalServiceId();
    if (extServiceId) {
      payload.externalServiceId = extServiceId;
    }

    return payload;
  }

  saveRequest(
  ): Observable<SaveRequestResponse> {
    const patient = this.patientService.selectedPatient();
    const doctor = this.selectedReferrerDoctor();

    if (!patient) {
      return throwError(() => new Error('Missing required data for save request'));
    }

    if (!doctor) {
      return throwError(() => new Error('Missing required data for save request'));
    }

    const payload = this.buildSaveRequestPayload(patient, doctor);

    if (!payload) {
      return throwError(() => new Error('Missing required data for save request'));
    }

    this.loadingStates.set({ ...this.loadingStates(), savingRequest: true });

    return this.http.post<SaveRequestResponse>(`${this.apiUrl}/rest/document/save-request`, payload).pipe(
      tap((response) => {
        // submit-request component will handle the success message

      }),
      finalize(() => {
        this.loadingStates.set({ ...this.loadingStates(), savingRequest: false });
      })
    );
  }

  modifyRequest(documentId: number): Observable<SaveRequestResponse> {
    const patient = this.patientService.selectedPatient();
    const doctor = this.selectedReferrerDoctor();

    if (!patient) {
      return throwError(() => new Error('Missing required data for modify request'));
    }

    if (!doctor) {
      return throwError(() => new Error('Missing required data for modify request'));
    }

    const payload = this.buildSaveRequestPayload(patient, doctor);

    if (!payload) {
      return throwError(() => new Error('Missing required data for modify request'));
    }

    this.loadingStates.set({ ...this.loadingStates(), savingRequest: true });

    return this.http.put<SaveRequestResponse>(`${this.apiUrl}/rest/document/modify-request/${documentId}`, payload).pipe(
      tap((response) => {
        // submit-request component will handle the success message

      }),
      finalize(() => {
        this.loadingStates.set({ ...this.loadingStates(), savingRequest: false });
      })
    );
  }

  resetRequestState(): void {
    this.selectedReferrerDoctor.set(null);
    this.selectedRelation.set(null);
    this.submitRequestFormData.set(null);
    this.currentFormCode.set(null);
    this.logNumber.set(null);
    this.caseId.set(null);
    this.externalServiceId.set(null);
    this.originalDoctorId.set(null);
    this.originalServicePointId.set(null);
    this.loadedDoctor.set(null);
    this.loadedServicePoint.set(null);
    this.submitterLocked.set(false);
  }

  getPdfForReferringRequest(req: GetPdfForReferringRequest): Observable<Blob> {
    const headers = new HttpHeaders().set('Accept', 'application/pdf');
    return this.http.post(
      `${this.apiUrl}/rest/document/getpdfforreffering`,
      req,
      { headers, responseType: 'blob' }
    );
  }
}
